<!--

  simple slideout panel component

  position: currently can be left, right, or bottom
  showPanel: whether to show panel initially or not
  triggerButtonClass: optional classes string for the trigger button

  named slots:
    trigger-button-contents - for overriding the default button

  left and right panels are full viewport height (aka a drawer)
  bottom panel is content height only and full width (aka a sheet)

  <slideout-panel
    position="right"
    :showPanel="true"
    triggerButtonClass="optional classes"
    ref="rightPanel"
    v-on:slideout-panel-opened="someMethod"
    v-on:slideout-panel-closed="someMethod2"
  >
    <span slot="trigger-button-contents">
      optional trigger button contents
    </span>

    panel contents here
  </slideout-panel>

-->

<template>
  <aside :class="{ loaded: loaded }">
    <button :class="triggerButtonClass" @click="openPanel">
      <slot name="trigger-button-contents"> Open {{ position }} panel </slot>
    </button>
    <div v-visible="open" class="slideout-panel" :class="panelPositionClass">
      <div class="slideout-panel-content">
        <div class="x-button" @click.prevent="closePanelByButton">
          <img loading="lazy" src="./close-x-black.svg" />
        </div>
        <slot>
          <!--panel content-->
        </slot>
        <div @click="closePanel">
          <slot name="additional-close-button" />
        </div>
      </div>
    </div>

    <template v-if="open">
      <div class="pagemask" @click="closePanelByButton" />
    </template>
    <nuxt-to-meta :nuxt2meta="head" />
  </aside>
</template>

<script lang="ts">
import {
  Component,
  Prop,
  Vue,
  Watch,
  toNative,
} from '~/utility/pu-class-decorator'

@Component({
  emits: [
    'slideout-panel-opened',
    'slideout-panel-closed',
    'slideout-panel-closed-by-button',
  ],
})
class PuSlideoutPanel extends Vue {
  open = false
  loaded = false

  @Prop({ default: 'right' })
  position!: string

  @Prop({ default: false })
  showPanel!: boolean

  @Prop()
  triggerButtonClass!: string[]

  created() {
    this.loaded = true
  }

  get head() {
    return {
      bodyAttrs: {
        class: this.open ? 'noscroll' : '',
      },
    }
  }

  openPanel() {
    this.open = true
  }

  closePanel() {
    this.open = false
  }

  closePanelByButton() {
    this.$emit('slideout-panel-closed-by-button')
    this.closePanel()
  }

  get panelPositionClass() {
    return this.open ? `${this.position} open` : this.position
  }

  @Watch('open')
  panelStateChanged(newValue: boolean) {
    this.$emit(newValue ? 'slideout-panel-opened' : 'slideout-panel-closed')
  }

  @Watch('showPanel')
  onShowPanelChanged(newValue: boolean): void {
    this.open = newValue
  }
}

export default toNative(PuSlideoutPanel)
</script>

<style scoped>
.pagemask {
  background: rgba(0, 0, 0, 0.6);
}

.slideout-panel {
  animation-duration: 0.3s;
  animation-fill-mode: both;
  animation-timing-function: ease-in-out;
  bottom: 0;
  content-visibility: hidden;
  display: flex;
  left: auto;
  position: fixed;
  right: auto;
  visibility: hidden;
  z-index: 14;
}

.slideout-panel.open {
  content-visibility: visible;
  top: 0;
  visibility: visible;
}

.noscroll {
  overflow: hidden;
}

.slideout-panel-content {
  background: white;
}

.x-button {
  align-items: center;
  background: transparent;
  border-radius: 0;
  border: none;
  box-shadow: none;
  color: white;
  display: flex;
  height: 2em;
  justify-content: center;
  line-height: 0;
  opacity: 0.4;
  padding: 0;
  position: absolute;
  right: 1em;
  top: 1em;
  width: 2em;
  cursor: pointer;
}

.x-button:active {
  opacity: 0.8;
  box-shadow: none;
  transform: none;
}

.right .slideout-panel-content,
.left .slideout-panel-content {
  width: 100vw;
  overflow-y: auto;
}

.bottom .slideout-panel-content {
  height: auto;
  max-height: 70vh;
  overflow-x: hidden;
  overflow-y: auto;
}

.slideout-panel.right {
  right: 0;
}

.slideout-panel.left {
  left: 0;
}

.bottom .slideout-panel-content {
  width: 100vw;
}

.slideout-panel.left {
  animation-name: slideout-panel-left-leave;
  justify-content: flex-start;
}

.slideout-panel.left.open {
  animation-name: slideout-panel-left-enter;
}

.slideout-panel.right {
  animation-name: slideout-panel-right-leave;
  justify-content: flex-end;
}

.slideout-panel.right.open {
  animation-name: slideout-panel-right-enter;
}

.slideout-panel.bottom {
  animation-name: slideout-panel-bottom-leave;
}

.slideout-panel.bottom.open {
  animation-name: slideout-panel-bottom-enter;
}

@keyframes slideout-panel-right-enter {
  from {
    transform: translate3d(100%, 0, 0);
  }
}

@keyframes slideout-panel-right-leave {
  to {
    transform: translate3d(100%, 0, 0);
  }
}

@keyframes slideout-panel-left-enter {
  from {
    transform: translate3d(-100%, 0, 0);
  }
}

@keyframes slideout-panel-left-leave {
  to {
    transform: translate3d(-100%, 0, 0);
  }
}

@keyframes slideout-panel-bottom-enter {
  from {
    transform: translate3d(0, 100%, 0);
  }
}

@keyframes slideout-panel-bottom-leave {
  to {
    transform: translate3d(0, 100%, 0);
  }
}
</style>
